/* src/utils/todayStats.js
 * Client-side derivations for the Today dashboard (/dashboard).
 * Single source: conversations[] from getWorkspaceConversations.
 * Shared with Pipeline (A3) later.
 */

import { dealStageLabel, DEAL_STAGES } from '../components/inbox/chips';

/** Parse Firestore / API timestamp to Date (or null). */
export function parseTimestamp(ts) {
  if (!ts) return null;
  if (typeof ts.toDate === 'function') return ts.toDate();
  if (ts._seconds != null) return new Date(ts._seconds * 1000);
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Sort key: lastMessageAt → updatedAt → createdAt. */
export function getSortTime(conv) {
  return (
    parseTimestamp(conv.lastMessageAt) ||
    parseTimestamp(conv.updatedAt) ||
    parseTimestamp(conv.createdAt) ||
    new Date(0)
  );
}

export function leadDisplayName(conv) {
  return conv.leadName || conv.leadEmail || 'Unknown lead';
}

function isBooked(conv) {
  return conv.status === 'booked' || conv.manuallyBooked === true;
}

function isOpenStage(stage) {
  return stage !== 'won' && stage !== 'lost';
}

export function formatMoney(value) {
  if (value == null || value <= 0) return null;
  return `$${Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

export function formatCompactMoney(value) {
  if (value == null || value <= 0) return null;
  if (value >= 1000) {
    const k = value / 1000;
    const rounded = k >= 10 ? Math.round(k) : Math.round(k * 10) / 10;
    return `$${rounded}k`;
  }
  return formatMoney(value);
}

/** Stat strip counts from conversations array. */
export function computeStatStrip(conversations) {
  const list = Array.isArray(conversations) ? conversations : [];
  let openPipelineValue = 0;

  list.forEach((conv) => {
    const stage = conv.dealStage || 'new';
    if (isOpenStage(stage) && conv.dealValue > 0) {
      openPipelineValue += conv.dealValue;
    }
  });

  return {
    totalLeads: list.length,
    needsYou: list.filter((c) => c.aiEnabled === false || c.reviewFlag === 'buying_held_suspicious').length,
    booked: list.filter(isBooked).length,
    openPipelineValue,
  };
}

/** Needs-you queue: operator paused, or buyer held for a human OK. */
export function filterNeedsYou(conversations, limit = 20) {
  const list = Array.isArray(conversations) ? conversations : [];
  return list
    .filter((c) => c.aiEnabled === false || c.reviewFlag === 'buying_held_suspicious')
    .sort((a, b) => getSortTime(b) - getSortTime(a))
    .slice(0, limit);
}

export function needsYouReason(conv) {
  if (conv?.reviewFlag === 'buying_held_suspicious') {
    return 'AI held — looks like a buyer';
  }
  return 'Operator paused - you are driving';
}

/**
 * Stage-change line mirrors ContextRail stage history rendering:
 * entry keys { from, to, dealValue, changedBy, changedAt } from leadActions.ts.
 */
export function formatStageChangeLine(entry, leadName) {
  const fromLabel = dealStageLabel(entry.from) || 'New';
  const toLabel = dealStageLabel(entry.to);
  const valuePart = entry.dealValue ? ` · $${entry.dealValue}` : '';
  return `Moved ${leadName} - ${fromLabel} → ${toLabel}${valuePart}`;
}

const OUTCOME_LABEL = { accepted: 'ACCEPTED ✓', test: 'TEST', skipped: 'SKIPPED' };

function formatConversionLine(conv, upload) {
  const name = leadDisplayName(conv);
  const valuePart = upload.dealValue ? `$${upload.dealValue}` : 'Won';
  const platforms = Array.isArray(upload.outcomes)
    ? upload.outcomes.map((o) => `${String(o.platform).toUpperCase()} ${OUTCOME_LABEL[o.outcome] || o.outcome}`).join(' · ')
    : '';
  return platforms
    ? `Uploaded ${valuePart} (${name}) - ${platforms}`
    : `Uploaded ${valuePart} (${name})`;
}

/** Flat activity log from real per-conversation fields only. */
export function buildActivityLog(conversations, limit = 15) {
  const list = Array.isArray(conversations) ? conversations : [];
  const events = [];

  list.forEach((conv) => {
    const name = leadDisplayName(conv);

    if (Array.isArray(conv.stageHistory)) {
      conv.stageHistory.forEach((entry, idx) => {
        const at = parseTimestamp(entry.changedAt);
        if (!at) return;
        if (entry.from === entry.to) return;
        events.push({
          id: `${conv.id}-stage-${idx}`,
          at,
          line: formatStageChangeLine(entry, name),
          kind: 'stage',
        });
      });
    }

    if (isBooked(conv)) {
      const at = parseTimestamp(conv.bookedAt) || parseTimestamp(conv.updatedAt);
      if (at) {
        events.push({
          id: `${conv.id}-booked`,
          at,
          line: `Booked ${name}`,
          kind: 'booked',
        });
      }
    }

    if (Array.isArray(conv.conversionUploads)) {
      conv.conversionUploads.forEach((upload, idx) => {
        const at = parseTimestamp(upload.processedAt);
        if (!at) return;
        events.push({
          id: `${conv.id}-conv-${idx}`,
          at,
          line: formatConversionLine(conv, upload),
          kind: 'conversion',
        });
      });
    }

    const createdAt = parseTimestamp(conv.createdAt);
    if (createdAt) {
      events.push({
        id: `${conv.id}-new`,
        at: createdAt,
        line: `New lead · ${name}`,
        kind: 'new',
      });
    }
  });

  return events.sort((a, b) => b.at - a.at).slice(0, limit);
}

/** Pipeline snapshot: count + dealValue sum per dealStage. */
export function computePipelineSnapshot(conversations) {
  const list = Array.isArray(conversations) ? conversations : [];
  const byStage = {};

  DEAL_STAGES.forEach((s) => {
    byStage[s.id] = { count: 0, value: 0 };
  });

  list.forEach((conv) => {
    const stage = conv.dealStage || 'new';
    const bucket = byStage[stage] || byStage.new;
    bucket.count += 1;
    if (conv.dealValue > 0) bucket.value += conv.dealValue;
  });

  return DEAL_STAGES.map((s) => ({
    id: s.id,
    label: s.label.toUpperCase(),
    count: byStage[s.id].count,
    value: byStage[s.id].value,
    tinted: s.id === 'won',
  }));
}

/** Format row time (same semantics as LeadListPane). */
export function formatRowTime(ts) {
  const d = ts instanceof Date ? ts : parseTimestamp(ts);
  if (!d) return '';
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'YESTERDAY';
  return d.toLocaleDateString();
}

/** Format log event time (HH:MM today, else short date). */
export function formatLogTime(ts) {
  const d = ts instanceof Date ? ts : parseTimestamp(ts);
  if (!d) return '';
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}
